import { useEffect, useState } from "react";

import { useMutation } from "@apollo/client/react";

import {
  ConversationStatusEnum,
  ConversationStopDocument,
  Status,
} from "@app_schema";

// Stopping holds at least this long, so a double click cannot fire a second stop.
const STOPPING_MIN_MS = 250;

// The composer's stop control. `running` follows the server's conversation
// status; `stopping` holds from the click until the turn has actually stopped,
// because a turn only notices the stop on its next streamed delta.
export const useConversationStop = (
  conversation?: { id: string; status: ConversationStatusEnum } | null,
) => {
  const [execute] = useMutation(ConversationStopDocument);
  const [requestedAt, setRequestedAt] = useState<number | null>(null);
  const running = conversation?.status === ConversationStatusEnum.Processing;

  useEffect(() => {
    if (requestedAt === null || running) return;

    const remaining = STOPPING_MIN_MS - (Date.now() - requestedAt);
    const timer = setTimeout(
      () => setRequestedAt(null),
      Math.max(0, remaining),
    );
    return () => clearTimeout(timer);
  }, [requestedAt, running]);

  const stop = () => {
    if (!conversation || requestedAt !== null) return;

    setRequestedAt(Date.now());
    // Nothing to stop yet (or the request failed): release the hold so the
    // button can be pressed again rather than pretending it worked.
    void execute({ variables: { id: conversation.id } }).then((result) => {
      if (result.data?.conversation.stop.status !== Status.Ok) {
        setRequestedAt(null);
      }
    });
  };

  return { running, stopping: requestedAt !== null, stop };
};
